import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@gonfalon/async/useDebouncedValue';
import { MemberAvatar } from '@gonfalon/members/MemberAvatar';
import { memberInitials } from '@gonfalon/members/memberInitials';
import type { StaticMessageId } from '@gonfalon/messages/ids';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useGetAiConversationHistoryQuery } from '@gonfalon/observability-graphql/hooks';
import { AgentConversationOrigin } from '@gonfalon/observability-graphql/schemas';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { LiveModeIndicator } from '@gonfalon/observability-ui/components/LiveModeIndicator';
import { SearchPagination } from '@gonfalon/observability-ui/components/SearchPagination';
import { Stack } from '@gonfalon/observability-ui/components/Stack';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { useMembers } from '@gonfalon/rest-api/hooks/useMembers';
import {
  Button,
  Group,
  IconButton,
  Input,
  SearchField,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Tag,
  TagGroup,
  TagList,
  Text,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import type { IconName } from '@launchpad-ui/icons';

import type { AgentInsightsHistoryView } from '../../AgentInsightsContext';
import { unBuildPrompt } from '../../agentInsightsUtils';
import { CONVERSATION_TAG_KEYS } from '../../conversationTags';
import { stripConversationTitleLinks } from '../../conversationTitle';
import { upsertOpenedSharedChat } from '../../internal/openedSharedChats';
import { formatRelativeDate } from '../../utils/formatRelativeDate';
import { getSafeHttpUrl } from '../../utils/safeUrl';

import * as styles from '../../internal/components/AgentInsights/AgentInsights.css';

type HistoryTab = 'chats' | 'runs';

type ChatsSecondaryFilter = 'mine' | 'shared';
type RunsSecondaryFilter = 'all' | 'factory' | 'flag-cleanups' | 'auto-remediations';

type ConversationTags = ReadonlyArray<{ key: string; value: string }> | null | undefined;

interface TabDefinition {
  id: HistoryTab;
  label: StaticMessageId;
  origin: AgentConversationOrigin;
}

const TAB_DEFINITIONS: readonly TabDefinition[] = [
  {
    id: 'chats',
    label: 'vega.history.chatsTab',
    origin: AgentConversationOrigin.Chat,
  },
  {
    id: 'runs',
    label: 'vega.history.runsTab',
    origin: AgentConversationOrigin.Run,
  },
];

interface SecondaryFilterOption<T extends string> {
  id: T;
  label: StaticMessageId;
}

export const CHATS_SECONDARY_FILTERS: ReadonlyArray<SecondaryFilterOption<ChatsSecondaryFilter>> = [
  { id: 'mine', label: 'vega.history.filterMine' },
  { id: 'shared', label: 'vega.history.filterShared' },
];

export const RUNS_SECONDARY_FILTERS: ReadonlyArray<SecondaryFilterOption<RunsSecondaryFilter>> = [
  { id: 'all', label: 'vega.history.filterAll' },
  { id: 'factory', label: 'vega.history.filterFactory' },
  { id: 'flag-cleanups', label: 'vega.history.filterFlagCleanups' },
  { id: 'auto-remediations', label: 'vega.history.filterAutoRemediations' },
];

function HistoryFilterGroup<T extends string>({
  ariaLabel,
  options,
  selectedId,
  onSelect,
}: {
  ariaLabel: string;
  options: ReadonlyArray<SecondaryFilterOption<T>>;
  selectedId: T;
  onSelect: (id: T) => void;
}) {
  return (
    <ToggleButtonGroup
      aria-label={ariaLabel}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[selectedId]}
      onSelectionChange={(keys) => {
        const next = options.find((option) => keys.has(option.id));
        if (next) {
          onSelect(next.id);
        }
      }}
    >
      {options.map((option) => (
        <ToggleButton key={option.id} id={option.id} size="small">
          {msg(option.label)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** Conversations fetched per History page, for both the Chats and Runs tabs. */
export const HISTORY_PAGE_SIZE = 20;

/** Build GraphQL variables for the active History tab + secondary filter. */
export function historyQueryVariables(args: {
  projectId: string;
  page: number;
  origin: AgentConversationOrigin;
  chatsFilter: ChatsSecondaryFilter;
  runsFilter: RunsSecondaryFilter;
  query?: string;
}): {
  project_id: string;
  count: number;
  page: number;
  origin: AgentConversationOrigin;
  query?: string;
  shared_only?: boolean;
  tag_key?: string;
  tag_value?: string;
  agent_kind?: string;
} {
  const base = {
    project_id: args.projectId,
    count: HISTORY_PAGE_SIZE,
    page: args.page,
    origin: args.origin,
    query: args.query,
  };

  if (args.origin === AgentConversationOrigin.Chat) {
    return { ...base, shared_only: args.chatsFilter === 'shared' };
  }

  switch (args.runsFilter) {
    case 'factory':
      return { ...base, agent_kind: 'factory-analyze' };
    case 'flag-cleanups':
      return { ...base, tag_key: CONVERSATION_TAG_KEYS.flagCleanupInProgress };
    case 'auto-remediations':
      return {
        ...base,
        tag_key: CONVERSATION_TAG_KEYS.autoInvestigation,
        tag_value: 'true',
      };
    case 'all':
    default:
      return base;
  }
}

export function historyEmptyMessage(args: {
  hasActiveSearch: boolean;
  tab: HistoryTab;
  chatsFilter: ChatsSecondaryFilter;
  runsFilter: RunsSecondaryFilter;
}): string {
  if (args.hasActiveSearch) {
    return args.tab === 'runs' ? msg('vega.history.noRunsMatchSearch') : msg('vega.history.noChatsMatchSearch');
  }
  if (args.tab === 'chats') {
    return args.chatsFilter === 'shared' ? msg('vega.history.noSharedChats') : msg('vega.history.noPersonalChats');
  }
  switch (args.runsFilter) {
    case 'factory':
      return msg('vega.history.noFactoryRuns');
    case 'flag-cleanups':
      return msg('vega.history.noFlagCleanups');
    case 'auto-remediations':
      return msg('vega.history.noAutoRemediations');
    case 'all':
    default:
      return msg('vega.history.noRuns');
  }
}

const TITLE_FALLBACK_LENGTH = 30;
const SEARCH_DEBOUNCE_MS = 250;
const HISTORY_POLL_INTERVAL_MS = 5000;

// A conversation still flagged `active` after this long almost certainly stalled
// (the agent crashed, a webhook never landed, etc.) rather than genuinely running,
// so we surface a warning instead of the live "in progress" indicator.
const STALE_ACTIVE_THRESHOLD_MS = 90 * 60 * 1000;

function isEmail(value: string): boolean {
  return value.includes('@');
}

function asHistoryTab(key: string | number | null | undefined): HistoryTab | null {
  if (key === 'chats' || key === 'runs') {
    return key;
  }
  return null;
}

function deriveTitle(conversation: Pick<ConversationSummary, 'title' | 'message_preview'>): string {
  const title = conversation.title?.trim();
  if (title) {
    return stripConversationTitleLinks(title);
  }
  const cleaned = stripConversationTitleLinks(unBuildPrompt(conversation.message_preview).trim());
  if (cleaned.length <= TITLE_FALLBACK_LENGTH) {
    return cleaned || msg('vega.history.untitledChat');
  }
  return `${cleaned.slice(0, TITLE_FALLBACK_LENGTH).trimEnd()}…`;
}

/** Map agent_kind + known workflow tags to a compact Runs kind badge label. */
export function deriveRunKindLabel(
  agentKind: string | null | undefined,
  tags: ConversationTags,
): { label: string; icon: IconName; tooltip: string } | undefined {
  if (agentKind === 'factory-analyze') {
    return {
      label: msg('vega.history.kindFactory'),
      icon: 'sparkle',
      tooltip: msg('vega.history.kindFactoryTooltip'),
    };
  }
  const hasAutoInvestigation = tags?.some(
    (t) => t.key === CONVERSATION_TAG_KEYS.autoInvestigation && t.value === 'true',
  );
  if (hasAutoInvestigation) {
    return {
      label: msg('vega.history.kindRemediation'),
      icon: 'sparkle',
      tooltip: msg('vega.history.kindRemediationTooltip'),
    };
  }
  const cleanupTag = tags?.find(
    (t) =>
      t.key === CONVERSATION_TAG_KEYS.flagCleanupInProgress || t.key === CONVERSATION_TAG_KEYS.automatedFlagCleanup,
  );
  if (cleanupTag) {
    const automated = cleanupTag.key === CONVERSATION_TAG_KEYS.automatedFlagCleanup;
    return {
      label: msg('vega.history.kindCleanup'),
      icon: 'flag-code',
      tooltip: automated
        ? msg('vega.history.kindAutomatedCleanupTooltip', { flagKey: cleanupTag.value })
        : msg('vega.history.kindCleanupTooltip', { flagKey: cleanupTag.value }),
    };
  }
  return undefined;
}

interface ConversationHistoryViewProps {
  onOpenConversation: (conversationId: string, title?: string | null) => void;
  initialView?: AgentInsightsHistoryView;
  currentUserId?: string;
  currentUserEmail?: string;
  aiChatLimitData?: {
    aiChatLimitStatus: {
      current_count: number;
      limit: number;
    } | null;
  };
  usageHref?: string;
}

export const ConversationHistoryView: React.FC<ConversationHistoryViewProps> = ({
  onOpenConversation,
  initialView,
  currentUserId,
  currentUserEmail,
  aiChatLimitData,
  usageHref,
}) => {
  const { projectId } = useProjectId();
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<HistoryTab>(initialView?.tab ?? 'chats');
  const [chatsFilter, setChatsFilter] = useState<ChatsSecondaryFilter>(initialView?.chatsFilter ?? 'mine');
  const [runsFilter, setRunsFilter] = useState<RunsSecondaryFilter>(initialView?.runsFilter ?? 'all');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const activeTabDefinition = TAB_DEFINITIONS.find((t) => t.id === activeTab) ?? TAB_DEFINITIONS[0];

  // Reset to the first page whenever the filter set changes so users aren't
  // stranded on an empty page when the result set shrinks.
  const filterKey = `${debouncedSearch}\0${activeTab}\0${chatsFilter}\0${runsFilter}`;
  const [seenFilterKey, setSeenFilterKey] = useState(filterKey);
  if (filterKey !== seenFilterKey) {
    setSeenFilterKey(filterKey);
    setPage(1);
  }

  const titleSearchVariable = debouncedSearch.trim() || undefined;

  const queryVariables = useMemo(
    () =>
      historyQueryVariables({
        projectId,
        page,
        origin: activeTabDefinition.origin,
        chatsFilter,
        runsFilter,
        query: titleSearchVariable,
      }),
    [projectId, page, activeTabDefinition.origin, chatsFilter, runsFilter, titleSearchVariable],
  );

  const { data, loading, error } = useGetAiConversationHistoryQuery({
    variables: queryVariables,
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
    // Poll while the view is mounted so in-flight conversations visibly flip
    // from "active" to their terminal status without a manual refresh. Apollo
    // pauses polling automatically while the tab is backgrounded.
    pollInterval: HISTORY_POLL_INTERVAL_MS,
  });

  // Memoize the conversations slice so dependents (e.g. the unresolved-ids
  // useMemo below) don't re-evaluate every render when Apollo returns the
  // same underlying data object.
  const conversations = useMemo(
    () => data?.aiConversationHistory?.conversations ?? [],
    [data?.aiConversationHistory?.conversations],
  );
  const totalCount = data?.aiConversationHistory?.totalCount ?? 0;

  const isOwnConversation = useCallback(
    (ownerValue: string) => ownerValue === currentUserId || ownerValue === currentUserEmail,
    [currentUserId, currentUserEmail],
  );

  // Pin not-mine chats into Recent so they appear after the user has opened them
  // (personal history alone doesn't include shared-with-me threads). Skip runs —
  // Recent is chats-only.
  const handleOpenConversation = useCallback(
    (conversation: ConversationSummary) => {
      if (
        projectId &&
        !isOwnConversation(conversation.user_email) &&
        conversation.origin !== AgentConversationOrigin.Run
      ) {
        upsertOpenedSharedChat(projectId, {
          conversation_id: conversation.conversation_id,
          initiated_at: conversation.initiated_at,
          last_user_message_at: conversation.last_user_message_at,
          message_preview: conversation.message_preview,
          title: conversation.title,
          tags: conversation.tags,
          origin: conversation.origin,
          agent_kind: conversation.agent_kind,
        });
      }
      onOpenConversation(conversation.conversation_id, conversation.title);
    },
    [projectId, isOwnConversation, onOpenConversation],
  );

  // Resolve member ids for any conversation we'll surface where the owner isn't
  // the current user — covers Shared where someone else's email/id might
  // appear on the card.
  const unresolvedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of conversations) {
      if (!isOwnConversation(c.user_email) && c.user_email && !isEmail(c.user_email)) {
        ids.add(c.user_email);
      }
    }
    return Array.from(ids);
  }, [conversations, isOwnConversation]);

  const { data: membersData } = useMembers(
    {
      params: {
        filter: { ids: unresolvedIds },
        limit: 50,
      },
    },
    { enabled: unresolvedIds.length > 0 },
  );

  const memberDisplayMap = useMemo(() => {
    const map = new Map<string, { displayName: string; email: string; initials: string | undefined }>();
    if (membersData?.items) {
      for (const m of membersData.items) {
        map.set(m._id, {
          displayName: m.firstName && m.lastName ? `${m.firstName} ${m.lastName}` : m.email,
          email: m.email,
          initials: memberInitials({
            firstName: m.firstName,
            lastName: m.lastName,
          }),
        });
      }
    }
    return map;
  }, [membersData]);

  const emptyMessage = historyEmptyMessage({
    hasActiveSearch: titleSearchVariable !== undefined,
    tab: activeTab,
    chatsFilter,
    runsFilter,
  });

  return (
    <Box height="full" display="flex" flexDirection="column">
      <Box px="16" pt="12" pb="8" flexShrink={0}>
        <SearchField
          aria-label={activeTab === 'runs' ? msg('vega.history.searchRunsLabel') : msg('vega.history.searchChatsLabel')}
          value={searchInput}
          onChange={setSearchInput}
          onClear={() => setSearchInput('')}
          style={{ width: '100%' }}
        >
          <Group>
            <Icon name="search" size="small" />
            <Input placeholder={msg('vega.history.searchPlaceholder')} />
            {searchInput.length > 0 && (
              <IconButton
                aria-label={msg('vega.history.clearSearchLabel')}
                variant="minimal"
                icon="cancel-circle-outline"
                size="small"
              />
            )}
          </Group>
        </SearchField>
      </Box>

      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(key) => {
          const next = asHistoryTab(key);
          if (next) {
            setActiveTab(next);
          }
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
        aria-label={msg('vega.history.tabsLabel')}
      >
        <TabList aria-label={msg('vega.history.categoriesLabel')} style={{ paddingInline: 16, flexShrink: 0 }}>
          {TAB_DEFINITIONS.map((tab) => (
            <Tab key={tab.id} id={tab.id}>
              {msg(tab.label)}
            </Tab>
          ))}
        </TabList>

        <Box px="16" pt="8" pb="4" flexShrink={0}>
          {activeTab === 'chats' ? (
            <HistoryFilterGroup
              ariaLabel={msg('vega.history.filterChatsLabel')}
              options={CHATS_SECONDARY_FILTERS}
              selectedId={chatsFilter}
              onSelect={setChatsFilter}
            />
          ) : (
            <HistoryFilterGroup
              ariaLabel={msg('vega.history.filterRunsLabel')}
              options={RUNS_SECONDARY_FILTERS}
              selectedId={runsFilter}
              onSelect={setRunsFilter}
            />
          )}
        </Box>

        {TAB_DEFINITIONS.map((tab) => (
          <TabPanel
            key={tab.id}
            id={tab.id}
            // LaunchPad TabPanel ships default margin/padding; zero them so the
            // list owns spacing (and matches main's full-bleed tab panels).
            style={{ flex: 1, minHeight: 0, overflow: 'auto', outline: 'none', margin: 0, padding: 0 }}
          >
            <ConversationList
              conversations={conversations}
              loading={loading}
              error={!!error}
              onOpen={handleOpenConversation}
              isOwnConversation={isOwnConversation}
              memberDisplayMap={memberDisplayMap}
              emptyMessage={emptyMessage}
              showKindBadge={tab.id === 'runs'}
            />
          </TabPanel>
        ))}
      </Tabs>

      <Box flexShrink={0}>
        {aiChatLimitData?.aiChatLimitStatus && (
          <Box p="8">
            <Button
              variant="minimal"
              style={{
                width: '100%',
                justifyContent: 'space-between',
                padding: '6px 8px',
                borderRadius: '6px',
                border: '1px solid var(--lp-color-border-ui-secondary)',
              }}
              onPress={() => {
                if (usageHref) {
                  window.open(usageHref, '_blank');
                }
              }}
            >
              <Stack direction="row" gap="6" alignItems="center" cssClass={styles.secondaryText}>
                <Icon name="warning" size="small" className={styles.secondaryIcon} />
                <Text size="small" className={styles.secondaryText}>
                  {aiChatLimitData.aiChatLimitStatus.limit > 0 ? (
                    <Message
                      id="vega.history.promptsUsedOfLimit"
                      values={{
                        count: aiChatLimitData.aiChatLimitStatus.current_count,
                        limit: aiChatLimitData.aiChatLimitStatus.limit,
                      }}
                    />
                  ) : (
                    <Message
                      id="vega.history.promptsUsed"
                      values={{ count: aiChatLimitData.aiChatLimitStatus.current_count }}
                    />
                  )}
                </Text>
              </Stack>
              {usageHref && <Icon name="link-external" size="small" slot="end-icon" />}
            </Button>
          </Box>
        )}

        <SearchPagination
          page={page}
          setPage={setPage}
          totalCount={totalCount}
          pageSize={HISTORY_PAGE_SIZE}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

interface MemberInfo {
  displayName: string;
  email: string;
  initials: string | undefined;
}

interface ConversationListProps {
  conversations: ConversationSummary[];
  loading: boolean;
  error: boolean;
  onOpen: (conversation: ConversationSummary) => void;
  isOwnConversation: (ownerValue: string) => boolean;
  memberDisplayMap: Map<string, MemberInfo>;
  emptyMessage: string;
  showKindBadge: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  loading,
  error,
  onOpen,
  isOwnConversation,
  memberDisplayMap,
  emptyMessage,
  showKindBadge,
}) => (
  <Box overflow="auto" flexGrow={1} p="16">
    {loading && (
      <Box padding="24" display="flex" justifyContent="center">
        <Text size="small">
          <Message id="vega.history.loadingConversations" />
        </Text>
      </Box>
    )}

    {!loading && error && (
      <Box padding="24" display="flex" justifyContent="center">
        <Text size="small" className={styles.secondaryText}>
          <Message id="vega.history.failedToLoad" />
        </Text>
      </Box>
    )}

    {!loading && !error && conversations.length === 0 && (
      <Box padding="24" display="flex" justifyContent="center">
        <Text size="small" className={styles.secondaryText}>
          {emptyMessage}
        </Text>
      </Box>
    )}

    {!loading && !error && conversations.length > 0 && (
      <Stack gap="4">
        {conversations.map((conversation) => (
          <ConversationCard
            key={conversation.conversation_id}
            conversation={conversation}
            onOpen={onOpen}
            isOwnConversation={isOwnConversation}
            memberDisplayMap={memberDisplayMap}
            showKindBadge={showKindBadge}
          />
        ))}
      </Stack>
    )}
  </Box>
);

interface ConversationSummary {
  conversation_id: string;
  user_email: string;
  user_message_count: number;
  agent_message_count: number;
  initiated_at: string;
  last_user_message_at?: string;
  message_preview: string;
  title?: string | null;
  status: string;
  is_shared: boolean;
  tags?: Array<{ key: string; value: string }> | null;
  agent_kind?: string | null;
  origin?: AgentConversationOrigin | null;
}

interface ConversationCardProps {
  conversation: ConversationSummary;
  onOpen: (conversation: ConversationSummary) => void;
  isOwnConversation: (ownerValue: string) => boolean;
  memberDisplayMap: Map<string, MemberInfo>;
  showKindBadge: boolean;
}

interface StatusVisual {
  // When true, render the pulsing live indicator instead of `icon`.
  live?: boolean;
  icon?: IconName;
  color: string;
  label: string;
}

// Mirrors the `agent_conversations.status` values surfaced on AIConversationSummary:
//   "active" (in-progress), "completed", "stopped" (user cancelled), "failed"
// `isStaleActive` flags conversations still marked active long past the point one
// could realistically be running (see STALE_ACTIVE_THRESHOLD_MS) — those get a
// warning rather than the live indicator. Falls back to a neutral completed
// indicator for any unknown future status so the card still renders something.
function getStatusVisual(status: string, isStaleActive: boolean): StatusVisual {
  switch (status) {
    case 'active':
      if (isStaleActive) {
        return {
          icon: 'warning-outline',
          color: 'var(--lp-color-text-feedback-warning)',
          label: msg('vega.history.statusStale'),
        };
      }
      return {
        live: true,
        color: 'var(--lp-color-text-feedback-info)',
        label: msg('vega.history.statusInProgress'),
      };
    case 'stopped':
      return {
        icon: 'stop-circle-outline',
        color: 'var(--lp-color-text-ui-secondary)',
        label: msg('vega.history.statusStopped'),
      };
    case 'failed':
      return {
        icon: 'warning-outline',
        color: 'var(--lp-color-text-feedback-warning)',
        label: msg('vega.history.statusFailed'),
      };
    case 'completed':
    default:
      return {
        icon: 'check-circle-outline',
        color: 'var(--lp-color-text-ui-secondary)',
        label: msg('vega.history.statusCompleted'),
      };
  }
}

interface PrTagVisual {
  label: string;
  tooltipLabel: string;
  icon: IconName;
}

const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  onOpen,
  isOwnConversation,
  memberDisplayMap,
  showKindBadge,
}) => {
  const isMine = isOwnConversation(conversation.user_email);
  const ownerValue = conversation.user_email;
  const memberInfo = memberDisplayMap.get(ownerValue);
  const ownerDisplayName = isEmail(ownerValue)
    ? ownerValue
    : (memberInfo?.displayName ?? msg('vega.history.anotherUser'));
  const avatarEmail = isEmail(ownerValue) ? ownerValue : memberInfo?.email;
  const avatarInitials = isEmail(ownerValue) ? undefined : memberInfo?.initials;
  const title = deriveTitle(conversation);
  const initiatedAtMs = new Date(conversation.initiated_at).getTime();
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const isStaleActive =
    conversation.status === 'active' &&
    Number.isFinite(initiatedAtMs) &&
    nowMs - initiatedAtMs > STALE_ACTIVE_THRESHOLD_MS;
  const statusVisual = getStatusVisual(conversation.status, isStaleActive);
  const kindBadge = showKindBadge ? deriveRunKindLabel(conversation.agent_kind, conversation.tags) : undefined;

  const prTag = conversation.tags?.find((t) => t.key === CONVERSATION_TAG_KEYS.prFiled);
  // The pr-filed value is attacker-controlled (set via the public tagConversation mutation), so it
  // must be validated as an http(s) URL before it is ever opened — otherwise a `javascript:` value
  // would run in the app origin when clicked. See SEC-8216.
  const prHref = getSafeHttpUrl(prTag?.value);
  const prMergedTag = conversation.tags?.find((t) => t.key === CONVERSATION_TAG_KEYS.prMerged);
  const prClosedTag = conversation.tags?.find((t) => t.key === CONVERSATION_TAG_KEYS.prClosed);

  let prTagVisual: PrTagVisual | undefined;
  if (prMergedTag) {
    prTagVisual = {
      label: msg('vega.history.prMerged'),
      tooltipLabel: msg('vega.history.prMergedTooltip'),
      icon: 'check-circle',
    };
  } else if (prClosedTag) {
    prTagVisual = {
      label: msg('vega.history.prClosed'),
      tooltipLabel: msg('vega.history.prClosedTooltip'),
      icon: 'cancel',
    };
  } else if (prTag) {
    prTagVisual = {
      label: msg('vega.history.prOpened'),
      tooltipLabel: msg('vega.history.prOpenedTooltip'),
      icon: 'arrow-merge',
    };
  }

  return (
    <Box
      p="8"
      borderRadius="8"
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      gap="8"
      cssClass={styles.conversationCard}
      onClick={(event) => {
        // PR chips render as links; don't also open the conversation.
        const target = event.target;
        const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
        if (element?.closest('a')) {
          return;
        }
        onOpen(conversation);
      }}
    >
      <Box
        flexShrink={0}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '20px',
          width: '16px',
        }}
      >
        <TooltipTrigger>
          <Button
            variant="minimal"
            aria-label={statusVisual.label}
            style={{
              padding: 0,
              minWidth: 0,
              minHeight: 0,
              background: 'transparent',
              border: 'none',
              color: statusVisual.color,
              display: 'inline-flex',
              cursor: 'pointer',
            }}
          >
            {statusVisual.live ? (
              <LiveModeIndicator />
            ) : (
              statusVisual.icon && <Icon name={statusVisual.icon} size="small" />
            )}
          </Button>
          <Tooltip placement="bottom">{statusVisual.label}</Tooltip>
        </TooltipTrigger>
      </Box>

      <Box display="flex" flexDirection="column" gap="4" flexGrow={1} style={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap="8">
          <Text size="small" bold className={styles.truncatedText}>
            {title}
          </Text>
          {!isMine && (
            <Box
              display="flex"
              alignItems="center"
              flexShrink={0}
              title={msg('vega.history.sharedByLabel', { owner: ownerDisplayName })}
            >
              <MemberAvatar
                email={avatarEmail}
                initials={avatarInitials}
                size="small"
                ariaLabel={msg('vega.history.sharedByLabel', { owner: ownerDisplayName })}
              />
            </Box>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap="6" style={{ flexWrap: 'wrap' }}>
          <Text size="small" className={styles.secondaryText}>
            {formatRelativeDate(conversation.initiated_at)}
          </Text>

          {prTagVisual && prHref && (
            <ConversationTagChip
              icon={prTagVisual.icon}
              label={prTagVisual.label}
              tooltip={prTagVisual.tooltipLabel}
              href={prHref}
            />
          )}

          {kindBadge && (
            <ConversationTagChip icon={kindBadge.icon} label={kindBadge.label} tooltip={kindBadge.tooltip} />
          )}

          {/* Shared chip is for Chats only — Runs already show a kind badge. */}
          {!isMine && !showKindBadge && (
            <ConversationTagChip icon="link" label={msg('vega.history.shared')} tooltip={msg('vega.history.shared')} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

interface ConversationTagChipProps {
  icon: IconName;
  label: string;
  tooltip: ReactNode;
  href?: string;
}

const ConversationTagChip: React.FC<ConversationTagChipProps> = ({ icon, label, tooltip, href }) => {
  const chip = (
    <TagGroup aria-label={label}>
      <TagList>
        <TooltipTrigger>
          <Tag
            size="small"
            textValue={label}
            href={href}
            target={href ? '_blank' : undefined}
            rel={href ? 'noopener noreferrer' : undefined}
          >
            <Icon name={icon} size="small" /> {label}
          </Tag>
          <Tooltip placement="bottom">{tooltip}</Tooltip>
        </TooltipTrigger>
      </TagList>
    </TagGroup>
  );

  if (!href) {
    return chip;
  }

  // LaunchPad Tag ignores onClick (React Aria press). Stop the native click on a
  // regular element so opening the PR does not also open the conversation card.
  return <Box onClick={(event) => event.stopPropagation()}>{chip}</Box>;
};
